/**
 * SQLite Client — database connection for app metadata storage.
 *
 * This stores APP METADATA only (transfer history, share records).
 * NOT torrent data (pieces, disk I/O) — that's engine-owned.
 *
 * Mobile: @op-engineering/op-sqlite (production SQLite via JSI direct execution with WAL mode)
 * Web/tests: InMemoryDB fallback (no native SQLite in browser)
 */

export interface DatabaseConnection {
  execute: (query: string, params?: any[]) => { rows: { _array: any[] } };
}

let SQLiteConn: DatabaseConnection | null = null;

/**
 * In-memory fallback DB for web / testing environments.
 * Provides the same interface as the native SQLite client.
 */
class InMemoryDB implements DatabaseConnection {
  private store: Record<string, any[]> = {
    transfers: [],
  };

  execute(query: string, params?: any[]): { rows: { _array: any[] } } {
    if (query.includes('INSERT') || query.includes('REPLACE')) {
      if (params) {
        const [
          id,
          direction,
          filesJson,
          status,
          progress,
          bytesTransferred,
          bytesUploaded,
          totalBytes,
          magnetUri,
          destinationRef,
          resumeDataJson,
          torrentIdentityJson,
          revision,
          createdAt,
          updatedAt,
        ] = params;

        // Remove existing item to act as UPSERT
        this.store.transfers = this.store.transfers.filter((t) => t.id !== id);

        this.store.transfers.push({
          id,
          direction,
          filesJson,
          status,
          progress,
          bytesTransferred,
          bytesUploaded: bytesUploaded ?? 0,
          totalBytes,
          magnetUri: magnetUri ?? null,
          destinationRef: destinationRef ?? null,
          resumeDataJson: resumeDataJson ?? null,
          torrentIdentityJson: torrentIdentityJson ?? null,
          revision: revision ?? 0,
          createdAt,
          updatedAt,
        });
      }
      return { rows: { _array: [] } };
    }
    if (query.includes('UPDATE')) {
      if (params && params.length >= 2) {
        if (query.includes('resumeDataJson = ? WHERE id = ?')) {
          const [resumeDataJson, id] = params;
          const target = this.store.transfers.find((t) => t.id === id);
          if (target) {
            target.resumeDataJson = resumeDataJson;
            target.updatedAt = Date.now();
          }
        }
      }
      return { rows: { _array: [] } };
    }
    if (query.includes('DELETE')) {
      if (params && params.length > 0) {
        const id = params[0];
        this.store.transfers = this.store.transfers.filter((t) => t.id !== id);
      }
      return { rows: { _array: [] } };
    }
    if (query.includes('SELECT')) {
      if (params && params.length > 0 && query.includes('WHERE id = ?')) {
        const id = params[0];
        const match = this.store.transfers.filter((t) => t.id === id);
        return { rows: { _array: [...match] } };
      }
      let list = [...this.store.transfers];
      if (query.includes('ORDER BY createdAt DESC')) {
        list.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
      }
      return { rows: { _array: list } };
    }
    return { rows: { _array: [] } };
  }
}

const fallbackDB = new InMemoryDB();

export const getDBConnection = (): DatabaseConnection => {
  return SQLiteConn || fallbackDB;
};

/**
 * Initialize database schema.
 * Migrated to @op-engineering/op-sqlite (JSI direct synchronous execution with WAL mode).
 */
export const initDb = async (): Promise<void> => {
  if (!SQLiteConn) {
    if (typeof require === 'function') {
      try {
        const opSqlite = require('@op-engineering/op-sqlite');
        const openDb = opSqlite?.open ?? opSqlite?.openDatabase;
        if (typeof openDb === 'function') {
          const db = openDb({ name: 'share.db', location: 'default' });

          // Configure WAL mode & synchronous = NORMAL for high-performance JSI execution
          try {
            db.execute('PRAGMA journal_mode = WAL;');
            db.execute('PRAGMA synchronous = NORMAL;');
          } catch {
            // Pragma execution failure ignored if unsupported
          }

          SQLiteConn = {
            execute: (query: string, params?: any[]) => {
              const result = db.execute(query, params ?? []);
              const rowsArray = Array.isArray(result?.rows?._array)
                ? result.rows._array
                : Array.isArray(result?.rows)
                ? result.rows
                : [];
              return {
                rows: {
                  _array: rowsArray,
                },
              };
            },
          };
        }
      } catch {
        // Native SQLite not available (Web, tests) — fallback below
      }
    }
  }

  const conn = getDBConnection();
  conn.execute(`
    CREATE TABLE IF NOT EXISTS transfers (
      id TEXT PRIMARY KEY,
      direction TEXT,
      filesJson TEXT,
      status TEXT,
      progress REAL,
      bytesTransferred INTEGER,
      bytesUploaded INTEGER DEFAULT 0,
      totalBytes INTEGER,
      magnetUri TEXT,
      destinationRef TEXT,
      resumeDataJson TEXT,
      torrentIdentityJson TEXT,
      revision INTEGER DEFAULT 0,
      createdAt INTEGER,
      updatedAt INTEGER
    );
  `);

  // Migration: add torrentIdentityJson to existing databases (safe no-op if already present)
  try {
    conn.execute(`ALTER TABLE transfers ADD COLUMN torrentIdentityJson TEXT;`);
  } catch {
    // Column already exists — silently ignore
  }
};
